import { useMemo } from 'react';
import { HttpAgent, AnonymousIdentity, type Identity, type ActorSubclass } from '@dfinity/agent';
import { useInternetIdentity } from './useInternetIdentity';
import { createActor } from '../declarations/backend';
import type { _SERVICE } from '../declarations/backend/backend.did';

// Local development canister ID (from .dfx/local/canister_ids.json)
const LOCAL_BACKEND_CANISTER_ID = 'uxrrr-q7777-77774-qaaaq-cai';
// Mainnet canister ID
const MAINNET_BACKEND_CANISTER_ID = '2ewbq-byaaa-aaaaf-qcu4q-cai';

export function useActor() {
  // Safely get identity - handle case where provider might not be ready
  let identity: Identity | null = null;
  let isAuthenticated = false;
  
  try {
    const iiContext = useInternetIdentity();
    identity = iiContext.identity;
    isAuthenticated = iiContext.isAuthenticated;
  } catch (error) {
    // If InternetIdentityProvider is not available, use AnonymousIdentity
    // This can happen during lazy loading or if provider isn't ready yet
    console.warn('[useActor] InternetIdentityProvider not available, using AnonymousIdentity');
    identity = new AnonymousIdentity();
    isAuthenticated = false;
  }

  const { actor, isFetching } = useMemo(() => {
    // Check if we're in local development - FORCE localhost detection
    const isLocalhost = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1' ||
                        window.location.hostname.includes('localhost');
    const isLocalNetwork = import.meta.env.VITE_DFX_NETWORK !== 'ic';
    const isLocal = isLocalhost || isLocalNetwork;
    
    // FORCE local canister ID if on localhost - no fallback to mainnet
    let canisterId: string;
    if (import.meta.env.VITE_BACKEND_CANISTER_ID) {
      canisterId = import.meta.env.VITE_BACKEND_CANISTER_ID;
    } else if (isLocal) {
      canisterId = LOCAL_BACKEND_CANISTER_ID;
    } else {
      canisterId = MAINNET_BACKEND_CANISTER_ID;
    }
    
    // CRITICAL: Runtime validation - disable actor if wrong canister ID is used locally
    if (isLocal && canisterId === MAINNET_BACKEND_CANISTER_ID) {
      // Only show error in development
      if (import.meta.env.DEV) {
        const error = new Error(
          `[useActor] CRITICAL ERROR: Using mainnet canister ID (${MAINNET_BACKEND_CANISTER_ID}) on localhost! ` +
          `This should be ${LOCAL_BACKEND_CANISTER_ID}. Browser cache may be stale - clear cache and hard refresh.`
        );
        console.error(error);
        console.error('%c🚨 CACHE ERROR - CLEAR BROWSER CACHE AND HARD REFRESH 🚨', 
          'color: red; font-size: 20px; font-weight: bold;');
      }
      // Return null actor to prevent queries from running
      return { actor: null, isFetching: false };
    }
    
    // Log for debugging (only in development)
    if (isLocal || import.meta.env.DEV) {
      console.log('[useActor] Creating actor:', {
        isAuthenticated,
        hasIdentity: !!identity,
        isLocalhost,
        isLocalNetwork,
        isLocal,
        hostname: window.location.hostname,
        VITE_DFX_NETWORK: import.meta.env.VITE_DFX_NETWORK,
        canisterId,
        expectedLocal: LOCAL_BACKEND_CANISTER_ID,
        expectedMainnet: MAINNET_BACKEND_CANISTER_ID,
        usingCorrectId: isLocal ? canisterId === LOCAL_BACKEND_CANISTER_ID : canisterId === MAINNET_BACKEND_CANISTER_ID
      });
    }

    // Allow actor creation with AnonymousIdentity - needed for passcode entry without II
    // Use provided identity if available, otherwise use AnonymousIdentity
    const actorIdentity = identity || new AnonymousIdentity();
    
    const agentOptions = {
      host: isLocal ? 'http://127.0.0.1:4943' : 'https://icp0.io',
      identity: actorIdentity,
    };

    const agent = new HttpAgent(agentOptions);

    if (isLocal) {
      agent.fetchRootKey().catch(err => {
        // Only log in development
        if (import.meta.env.DEV) {
          console.warn('Unable to fetch root key (local only)', err);
        }
      });
    }

    try {
      const actor = createActor(canisterId, { agent }) as ActorSubclass<_SERVICE>;
      return { actor, isFetching: false };
    } catch (error) {
      console.error('Failed to create actor:', error);
      return { actor: null, isFetching: false };
    }
  }, [identity, isAuthenticated]);

  return { actor, isFetching } as {
    actor: ActorSubclass<_SERVICE> | null;
    isFetching: boolean;
  };
}

